import { useSyncExternalStore } from 'react';

/**
 * Routeur (US-13) : « je veux pouvoir revenir en arrière, rafraîchir sans rien
 * perdre, et envoyer le lien d'une recette ».
 *
 * Jusqu'ici `App.tsx` pilotait les écrans par un simple `useState<View>` : la
 * barre d'adresse ne changeait jamais, aucune entrée d'historique n'était
 * créée (`history.length` restait constant sur un parcours complet), le bouton
 * retour du navigateur quittait l'application, et « Annuler » depuis un import
 * ramenait sur l'écran précédemment affiché, sans rapport avec l'import. C'est
 * aussi le préalable de US-19 (redirection vers la connexion sur 401).
 *
 * **Routeur écrit ici plutôt qu'une bibliothèque.** ROADMAP.md §2 avait retenu
 * `react-router` ; décision inversée après examen du besoin réel. Sept routes
 * statiques, un seul paramètre, aucune route imbriquée, aucun chargement
 * différé, aucun `loader` — la surface utilisée d'une bibliothèque de routage
 * se réduirait à ce fichier. §5 CLAUDE.md tranche dans ce sens : « pas
 * d'abstraction anticipée … le code doit rester lisible, pas extensible ».
 * **Écarté :** `react-router` (dépendance et API très au-delà du besoin),
 * routage par fragment `#/…` (casse le repli SPA déjà en place dans
 * `server/src/app.ts` et les liens partagés).
 *
 * **`useSyncExternalStore` plutôt qu'un `useState` + `useEffect`.** L'URL est
 * un état extérieur à React, modifié par le navigateur (`popstate`) autant que
 * par l'application (`navigate`) : c'est exactement ce que cette API décrit.
 * Elle évite le rendu intermédiaire avec une valeur périmée qu'un
 * `useEffect` produirait au montage.
 *
 * Le repli SPA côté serveur existe déjà (`app.setNotFoundHandler` renvoie
 * `index.html` pour toute URL non préfixée `/api/`) : aucune modification
 * serveur n'est nécessaire, et le service worker continue de servir les
 * fiches déjà visitées hors connexion.
 */

/**
 * Une navigation interne ne déclenche pas `popstate` (le navigateur ne
 * l'émet que pour les déplacements dans l'historique) : `navigate` publie
 * donc cet événement pour réveiller les abonnés.
 */
const NAVIGATE_EVENT = 'mlc:navigate';

function subscribe(onStoreChange: () => void): () => void {
  window.addEventListener('popstate', onStoreChange);
  window.addEventListener(NAVIGATE_EVENT, onStoreChange);
  return () => {
    window.removeEventListener('popstate', onStoreChange);
    window.removeEventListener(NAVIGATE_EVENT, onStoreChange);
  };
}

function readUrl(): string {
  return `${window.location.pathname}${window.location.search}`;
}

/** Rendu hors navigateur (aucun aujourd'hui, mais `useSyncExternalStore` l'exige). */
function readServerUrl(): string {
  return '/';
}

/** Chemin + paramètres de requête courants, re-rendu à chaque changement. */
export function useUrl(): string {
  return useSyncExternalStore(subscribe, readUrl, readServerUrl);
}

/**
 * Change d'écran. `replace` pour les changements qui ne méritent pas une
 * entrée d'historique — typiquement la frappe dans le champ de recherche,
 * qui sinon empilerait une entrée par caractère et rendrait le bouton
 * retour inutilisable.
 */
export function navigate(to: string, options: { replace?: boolean } = {}): void {
  if (readUrl() === to) return;
  if (options.replace === true) window.history.replaceState(null, '', to);
  else window.history.pushState(null, '', to);
  window.dispatchEvent(new Event(NAVIGATE_EVENT));
}

// ----------------------------------------------------------------------------
// Chemins
// ----------------------------------------------------------------------------

/**
 * Constructeurs de chemins : aucune URL n'est écrite en dur ailleurs dans
 * l'application, pour que `matchRoute` et les liens ne puissent pas diverger.
 * Chemins en français, comme les libellés (§5 CLAUDE.md).
 */
export const paths = {
  library: '/',
  archives: '/archives',
  newRecipe: '/nouvelle',
  importUrl: '/importer/url',
  importPaste: '/importer/texte',
  importPhoto: '/importer/photo',
  /**
   * Le sas (US-71) : ce qu'on a mis de côté et pas encore gardé. Chemin court
   * — il s'ouvre depuis la barre de navigation, plusieurs fois par semaine, et
   * il se tape à la main quand on veut y revenir vite.
   */
  importInbox: '/sas',
  settings: '/reglages',
  /** Mot de passe oublié (US-53). Atteignable sans session, comme un lien de partage. */
  resetPassword: '/mot-de-passe',
  /**
   * L'arrivée d'un invité (US-103). Atteignable sans session, pour la même
   * raison que la réinitialisation : celui qui l'ouvre vient précisément
   * parce qu'il n'a pas de compte. Le jeton voyage en paramètre de requête,
   * comme celui du mot de passe — c'est le contenu d'un lien reçu par
   * courrier, pas un segment de chemin que l'on tape.
   */
  invitation: '/invitation',
  shopping: '/courses',
  journal: '/journal',
  /** Les collections ont enfin un écran (US-63) : elles étaient invisibles hors de la barre de filtres. */
  collections: '/collections',
  /**
   * Le fonds Thermomix (US-97) : deux cents recettes écrites pour
   * l'application, qui ont désormais leur écran plutôt que les trois premiers
   * de l'accueil. Une adresse comme les autres — donc partageable, restituée
   * par le bouton retour, et atteignable directement.
   */
  fonds: '/fonds',
  mealPlan: '/semaine',
  sharedRecipe: (token: string): string => `/partage/${encodeURIComponent(token)}`,
  recipe: (recipeId: string): string => `/recette/${encodeURIComponent(recipeId)}`,
  editRecipe: (recipeId: string): string => `/recette/${encodeURIComponent(recipeId)}/modifier`,
  cook: (recipeId: string): string => `/recette/${encodeURIComponent(recipeId)}/cuisiner`,
} as const;

// ----------------------------------------------------------------------------
// Correspondance
// ----------------------------------------------------------------------------

export type Route =
  | { name: 'library' }
  | { name: 'archives' }
  | { name: 'new-recipe' }
  | { name: 'import-url' }
  | { name: 'import-paste' }
  | { name: 'import-photo' }
  | { name: 'import-inbox' }
  | { name: 'settings' }
  | { name: 'reset-password' }
  | { name: 'invitation' }
  | { name: 'shopping' }
  | { name: 'journal' }
  | { name: 'collections' }
  | { name: 'fonds' }
  | { name: 'meal-plan' }
  | { name: 'shared-recipe'; token: string }
  | { name: 'recipe'; recipeId: string }
  | { name: 'edit-recipe'; recipeId: string }
  | { name: 'cook'; recipeId: string }
  | { name: 'not-found' };

/**
 * Union discriminée plutôt qu'une table de motifs : les routes sont connues à
 * l'avance, et TypeScript vérifie alors qu'`App.tsx` les traite toutes.
 * Un identifiant vide (`/recette/`) tombe en `not-found` plutôt que de
 * déclencher une requête serveur vouée à échouer.
 */
export function matchRoute(url: string): Route {
  const pathname = url.split('?')[0] ?? '/';
  // Chemin normalisé : une barre oblique finale ne crée pas une route autre
  // (`/archives/` et `/archives` sont le même écran).
  const trimmed = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

  if (trimmed === paths.library) return { name: 'library' };
  if (trimmed === paths.archives) return { name: 'archives' };
  if (trimmed === paths.newRecipe) return { name: 'new-recipe' };
  if (trimmed === paths.importUrl) return { name: 'import-url' };
  if (trimmed === paths.importPaste) return { name: 'import-paste' };
  if (trimmed === paths.importPhoto) return { name: 'import-photo' };
  if (trimmed === paths.importInbox) return { name: 'import-inbox' };
  if (trimmed === paths.settings) return { name: 'settings' };
  if (trimmed === paths.resetPassword) return { name: 'reset-password' };
  if (trimmed === paths.invitation) return { name: 'invitation' };
  if (trimmed === paths.shopping) return { name: 'shopping' };
  if (trimmed === paths.journal) return { name: 'journal' };
  if (trimmed === paths.collections) return { name: 'collections' };
  if (trimmed === paths.fonds) return { name: 'fonds' };
  if (trimmed === paths.mealPlan) return { name: 'meal-plan' };

  const segments = trimmed.split('/').filter((segment) => segment !== '');
  // Lien de partage (US-33) : le seul écran atteignable sans être connecté.
  if (segments[0] === 'partage' && segments.length === 2 && segments[1] !== undefined && segments[1] !== '') {
    return { name: 'shared-recipe', token: decodeURIComponent(segments[1]) };
  }
  if (segments[0] === 'recette' && segments[1] !== undefined && segments[1] !== '') {
    const recipeId = decodeURIComponent(segments[1]);
    if (segments.length === 2) return { name: 'recipe', recipeId };
    if (segments.length === 3 && segments[2] === 'cuisiner') return { name: 'cook', recipeId };
    if (segments.length === 3 && segments[2] === 'modifier') return { name: 'edit-recipe', recipeId };
  }

  return { name: 'not-found' };
}

/** Route courante, recalculée à chaque changement d'URL. */
export function useRoute(): Route {
  return matchRoute(useUrl());
}

// ----------------------------------------------------------------------------
// Paramètres de requête
// ----------------------------------------------------------------------------

/**
 * Recherche et filtre vivent dans l'URL (US-13) et non plus dans l'état local
 * de `RecipeList` : le retour arrière restitue la liste telle qu'elle était,
 * et une recherche est partageable. Renvoie `''` plutôt que `null` — un
 * paramètre absent et un paramètre vide décrivent la même chose ici, et
 * `''` évite un `?? ''` à chaque appel.
 */
export function useSearchParam(name: string): string {
  const url = useUrl();
  const queryString = url.split('?')[1];
  if (queryString === undefined) return '';
  return new URLSearchParams(queryString).get(name) ?? '';
}

/**
 * Reconstruit l'URL courante en modifiant un seul paramètre. Une valeur vide
 * retire le paramètre plutôt que de laisser `?q=` traîner dans la barre
 * d'adresse.
 */
export function withSearchParam(url: string, name: string, value: string): string {
  const [pathname = '/', queryString] = url.split('?');
  const params = new URLSearchParams(queryString ?? '');
  if (value === '') params.delete(name);
  else params.set(name, value);
  const next = params.toString();
  return next === '' ? pathname : `${pathname}?${next}`;
}

/**
 * Même chose pour plusieurs paramètres à la fois (US-21) : appliquer une
 * collection change le terme de recherche, le statut et les étiquettes d'un
 * seul geste. Enchaîner `withSearchParam` produirait autant d'états
 * intermédiaires, et donc autant de requêtes au serveur.
 */
export function withSearchParams(url: string, changes: Record<string, string>): string {
  return Object.entries(changes).reduce((current, [name, value]) => withSearchParam(current, name, value), url);
}

// ----------------------------------------------------------------------------
// Lien
// ----------------------------------------------------------------------------

export interface LinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
  replace?: boolean;
}

/**
 * Vrai `<a href>` — c'est tout l'intérêt du routeur pour la bibliothèque, dont
 * les cartes étaient jusqu'ici des `<button>` : ouverture dans un nouvel
 * onglet, clic milieu, « copier l'adresse du lien », aperçu de la cible au
 * survol, et curseur pointeur sans avoir à le forcer.
 *
 * Seul le clic gauche simple est intercepté : un clic avec ⌘/Ctrl/Maj, un
 * clic milieu ou un lien avec une cible explicite doivent garder le
 * comportement natif du navigateur.
 */
export function Link({ to, replace = false, onClick, target, ...rest }: LinkProps): React.JSX.Element {
  function handleClick(event: React.MouseEvent<HTMLAnchorElement>): void {
    onClick?.(event);
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (target !== undefined && target !== '_self') return;
    event.preventDefault();
    navigate(to, { replace });
  }

  return <a href={to} target={target} onClick={handleClick} {...rest} />;
}
